import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import DraenContentSection from '../components/DraenContentSection';
import KloakspulingContentSection from '../components/KloakspulingContentSection';
import StoebearbejdeContentSection from '../components/StoebearbejdeContentSection';
import BelaegningContentSection from '../components/BelaegningContentSection';
import SEO from '../components/SEO';
import { getImageMeta } from '../lib/mediaDescriptions';
import { getServiceMeta } from '../lib/servicesMeta';

function renderIntroWithHomeLink(intro) {
  if (!intro) return null;
  const targetWithAps = 'Entreprenør Jan Nørgaard ApS';
  const targetShort = 'Entreprenør Jan Nørgaard';
  
  let match = null;
  if (intro.includes(targetWithAps)) {
    match = targetWithAps;
  } else if (intro.includes(targetShort)) {
    match = targetShort;
  }
  
  if (match) {
    const idx = intro.indexOf(match);
    const before = intro.substring(0, idx);
    const after = intro.substring(idx + match.length);
    return (
      <>
        {before}
        <Link
          to="/"
          className="text-white underline decoration-brand-orange-400 decoration-2 underline-offset-4 font-bold hover:text-brand-orange-400 transition-colors"
          title="Entreprenør Jan Nørgaard - Til forsiden"
        >
          {match}
        </Link>
        {after}
      </>
    );
  }

  // Hvis firmanavnet ikke findes i introen, introduceres det i starten som første brødtekstlink
  const firstCharLower = intro.charAt(0).toLowerCase() + intro.slice(1);
  return (
    <>
      Hos{' '}
      <Link
        to="/"
        className="text-white underline decoration-brand-orange-400 decoration-2 underline-offset-4 font-bold hover:text-brand-orange-400 transition-colors"
        title="Entreprenør Jan Nørgaard - Til forsiden"
      >
        Entreprenør Jan Nørgaard
      </Link>{' '}
      tilbyder vi {firstCharLower}
    </>
  );
}

function cleanDescription(html) {
  if (!html) return '';
  // Fjern eventuelle links til forsiden i beskrivelsen, så der kun er ét link til forsiden i brødteksten
  return html.replace(/<a\b[^>]*href=["']\/?["'][^>]*>(.*?)<\/a>/gi, '$1');
}

function getHeadingBySlug(slug, title) {
  const headings = {
    'udgravning-af-soe': 'Etablering og udgravning af søer',
    'draen-etablering-reparation': 'Dræn der beskytter hus og grund mod fugt',
    'pavillon-anlaegsarbejde': 'Anlægsarbejde og pavilloner',
    'kloakspuling': 'Professionel kloakspuling der får vandet til at løbe frit',
    'nedsivningsanlaeg': 'Etablering af nedsivningsanlæg',
    'stoebearbejde': 'Støbearbejde i Sommersted med et solidt resultat',
    'minirenseanlaeg': 'Montering af minirenseanlæg',
    'udgravning-til-byggeri': 'Udgravning og jordarbejde til byggeri',
    'reparation-etablering-af-ridebaner': 'Etablering og renovering af ridebaner',
    'mini-vaadomraader': 'Etablering af mini-vådområder',
    'omfangsdraen': 'Etablering af omfangsdræn',
    'rottespaerre': 'Sikring med rottespærre',
    'kloakmester': 'Autoriseret kloakmesterarbejde',
    'laserafretning': 'Millimeterpræcis laserafretning',
    'kloakseparering': 'Udførelse af kloakseparering',
    'tv-inspektion': 'Grundig TV-inspektion af kloak',
    'belaegning': 'Professionelt belægningsarbejde'
  };

  return headings[slug] || `Om ${title || 'ydelsen'}`;
}

function getWhatWeOfferBulletpoints(slug) {
  const bulletpoints = {
    'udgravning-af-soe': [
      'Etablering af nye havedamme og vildtsøer',
      'Udgravning af regnvandsbassiner og LAR-anlæg',
      'Oprensning og udvidelse af eksisterende søer',
      'Rådgivning om placering, dybde og skråningsvinkler',
      'Håndtering og genindbygning af opgravede jordmasser'
    ],
    'draen-etablering-reparation': [
      'Vurdering af fugt, terræn, jord og rørforhold',
      'Etablering af dræn omkring hus, sokkel og have',
      'Omfangsdræn langs husets fundament',
      'Drænspuling og genopretning af frit gennemløb',
      'Reparation og fornyelse af eksisterende drænrør',
      'Autoriseret tilslutning og professionel retablering'
    ],
    'pavillon-anlaegsarbejde': [
      'Opbygning og montering af havepavilloner',
      'Etablering af hyggelige terrasser og uderum',
      'Jordregulering og klargøring af haveanlæg',
      'Etablering af støttemure og højbede',
      'Professionel rådgivning om materialevalg og design'
    ],
    'kloakspuling': [
      'Akut spuling af stoppede kloakker, toiletter og vaske',
      'Rensning af tilstoppede brønde og stikledninger',
      'Forebyggende spuleaftaler for private og erhverv',
      'Fjernelse af fedt, aflejringer og rødder i rørene',
      'Spuling i forbindelse med TV-inspektion'
    ],
    'nedsivningsanlaeg': [
      'Komplet etablering af godkendte nedsivningsanlæg',
      'Jordbundsprøver og udførelse af sigtetest (nedsivningsevne)',
      'Nedgravning og tilslutning af bundfældningstanke (trixtanke)',
      'Udlægning af siverør og etablering af sandfilter',
      'Håndtering af ansøgning og myndighedskontakt'
    ],
    'stoebearbejde': [
      'Støbning af fundamenter til huse, tilbygninger og garager',
      'Etablering af terrændæk og kældergulve',
      'Støbning af sokler, støttemure og trapper',
      'Armering og professionel isolering af betongulve',
      'Betonarbejde til landbrug og erhvervsbyggeri'
    ],
    'minirenseanlaeg': [
      'Salg og montering af godkendte biologiske minirenseanlæg',
      'Udgravning og nedgravning af anlæggets tanke',
      'Tilslutning til ejendommens eksisterende kloaksystem',
      'Etablering af udløb to dræn, vandløb eller nedsivning',
      'Udarbejdelse af ansøgning om udledningstilladelse'
    ],
    'udgravning-til-byggeri': [
      'Afrømning af muldjord og terrænregulering',
      'Præcis udgravning til fundamenter og kældre',
      'Opbygning og komprimering af stabil sandpude',
      'Bortskaffelse eller genindbygning af overskudsjord',
      'Jordarbejde med moderne, GPS-styrede maskiner'
    ],
    'reparation-etablering-af-ridebaner': [
      'Etablering af nye udendørs og indendørs ridebaner',
      'Opbygning af korrekt drænende og fjedrende bærelag',
      'Udlægning af specialtilpasset toplag (sand, fiber, flis)',
      'Laserafretning for en 100% plan og jævn overflade',
      'Renovering og udbedring af bløde, hårde eller våde baner'
    ],
    'mini-vaadomraader': [
      'Udgravning af sedimentationsbassiner til drænvand',
      'Etablering af filtermatricer og kontrollerede ind-/udløb',
      'Terrænregulering og tilpasning til det omgivende landskab',
      'Rådgivning om tilskudsordninger til mini-vådområder',
      'Professionel udførelse i tæt samarbejde med udtagningskonsulenter'
    ],
    'omfangsdraen': [
      'Udgravning langs husets fundament og kældervægge',
      'Etablering af drænrør og drænplader',
      'Udvendig isolering af kældervægge (Sundolitt / isolering)',
      'Professionel fugtsikring og tætning af sokkel',
      'Tilslutning til godkendt sandfangsbrønd og pumpebrønd'
    ],
    'rottespaerre': [
      'Montering af godkendte rottespærrer i rustfrit stål',
      'Montering i både plast- og betonbrønde',
      'Certificeret installation af autoriseret kloakmester',
      'Årligt lovpligtigt serviceeftersyn og rengøring af spærren',
      'Rådgivning om forebyggelse af rotteproblemer'
    ],
    'kloakmester': [
      'Nyanlæg og renovering af alle typer kloaksystemer',
      'Reparation af forsikringsskader på kloakledninger',
      'Etablering af højvandslukkere og pumpebrønde',
      'Tilslutning af nyt byggeri til det offentlige kloaknet',
      'Lovpligtig kvalitetssikring og dokumentation af alt arbejde'
    ],
    'laserafretning': [
      'Præcis afretning af sand, grus og stabilgrus',
      'Forberedelse af underlag til industrigulve og lagerhaller',
      'Afretning til store parkeringspladser og belægningsarealer',
      'Laserstyret afretning af ridebaner og sportspladser',
      'Hurtig og effektiv udførelse med specialmaskiner'
    ],
    'kloakseparering': [
      'Komplet adskillelse af regnvand og spildevand på grunden',
      'Nedgravning af nye, separate rørledninger',
      'Etablering af faskiner eller regnvandsbede til lokal afledning (LAR)',
      'Tilslutning til de kommunale skelbrønde',
      'Fuld dokumentation og færdigmelding til kommunen'
    ],
    'tv-inspektion': [
      'Kamera-undersøgelse af kloakrør og stikledninger',
      'Lokalisering af brud, rørskred, sætninger og rødder',
      'Opsporing af rottehuller og ulovlige tilslutninger',
      'Udarbejdelse af detaljeret fotodokumentation og servicerapport',
      'TV-inspektion før huskøb eller i forbindelse med forsikringssager'
    ],
    'belaegning': [
      'Etablering af nye indkørsler, terrasser og gangstier',
      'Lægning af herregårdssten, betonfliser, granit og brosten',
      'Opbygning af stabilt og bæredygtigt underlag (bærelag)',
      'Etablering af støttemure, trapper og kantsten',
      'Omlægning og reparation af sunkne fliser og kørespor'
    ]
  };

  return bulletpoints[slug] || null;
}

export default function YdelseDetalje() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [galleries, setGalleries] = useState([]);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    // Fetch the single service by slug
    fetch(`https://api.dev.auxo.dk/public/by-slug/da/${slug}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error('Ydelsen blev ikke fundet');
        }
        return res.json();
      })
      .then((data) => {
        if (data && data.collectionName === 'ydelser') {
          setService(data);
        } else {
          throw new Error('Ugyldig ydelse');
        }
      })
      .catch((err) => {
        console.error('Error fetching service:', err);
        setError(err.message);
      })
      .finally(() => {
        setLoading(false);
      });

    // Fetch galleries to display matching work
    fetch('https://api.dev.auxo.dk/public/content/galleri')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setGalleries(data);
        }
      })
      .catch((err) => console.error('Error fetching galleries:', err));
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-navy-50">
        <div className="text-center">
          <i className="fa-solid fa-spinner-third fa-spin text-4xl text-brand-orange-500" />
          <p className="mt-4 text-sm font-semibold text-brand-navy-600">Henter ydelse...</p>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-navy-50 px-4">
        <SEO
          title="Ydelsen blev ikke fundet | Jan Nørgaard"
          description="Vi kunne desværre ikke finde den ydelse, du leder efter hos Entreprenør Jan Nørgaard ApS."
          noindex={true}
        />
        <div className="text-center max-w-md">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-red-100 text-red-600 mb-4">
            <i className="fa-solid fa-circle-exclamation text-2xl" />
          </div>
          <h1 className="font-display text-2xl font-bold text-brand-navy-900">Ydelsen blev ikke fundet</h1>
          <p className="mt-2 text-brand-navy-600">
            Vi kunne desværre ikke finde den ydelse, du leder efter. Den er muligvis blevet flyttet eller slettet.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/referencer"
              className="rounded bg-brand-navy-900 px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-brand-navy-800"
            >
              Se vores referencer
            </Link>
            <Link
              to="/"
              className="rounded border border-brand-navy-200 bg-white px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-brand-navy-900 transition-colors hover:bg-brand-navy-100"
            >
              Gå til forside
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Find matching gallery based on gallery_slug
  const matchedGallery = galleries.find(g => 
    g.slug?.da === service.gallery_slug || 
    g.slug?.da?.includes(service.gallery_slug) ||
    service.gallery_slug?.includes(g.slug?.da) ||
    (service.gallery_slug === 'gravearbejde' && g.slug?.da === 'udgravning-jordarbejde')
  );

  const heroImageUrl = matchedGallery?.images?.[0] || 'https://assets.auxodev.dk/assets/af349c16-3f04-4dd3-a862-642e24736c63/udgravning-minigraver-jordarbejde.jpg';
  const heroImageMeta = getImageMeta(heroImageUrl, service.title);
  const serviceMeta = getServiceMeta(slug, service);

  return (
    <div className="bg-brand-navy-50 min-h-screen pb-16">
      <SEO
        title={serviceMeta.title}
        description={serviceMeta.description}
        image={heroImageUrl.includes('https://assets.auxodev.dk/') && !heroImageUrl.includes('/cdn-cgi/image/') ? `https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/${heroImageUrl.replace('https://assets.auxodev.dk/', '')}` : heroImageUrl}
      />
      {/* Hero Section */}
      <section className="bg-brand-navy-900 text-white py-10 md:py-14 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-15">
          <img
            src={heroImageUrl.includes('https://assets.auxodev.dk/') && !heroImageUrl.includes('/cdn-cgi/image/') ? `https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/${heroImageUrl.replace('https://assets.auxodev.dk/', '')}` : heroImageUrl}
            alt={heroImageMeta.alt}
            title={heroImageMeta.title}
            className="h-full w-full object-cover"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
          {/* Breadcrumbs */}
          <nav className="mb-6 flex justify-center items-center gap-2 text-xs uppercase tracking-wider font-semibold text-brand-navy-300">
            <Link to="/" className="hover:text-brand-orange-400 transition-colors">Forside</Link>
            <span>/</span>
            <span className="text-white">Ydelser</span>
            <span>/</span>
            <span className="text-brand-orange-400">{service.title}</span>
          </nav>
          <div className="inline-flex items-center justify-center text-white mb-6">
            <i className={`${service.icon} text-4xl`} />
          </div>
          <h1 className="font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl md:text-5xl">
            {service.title}
          </h1>
          <p className="mt-6 max-w-3xl mx-auto text-lg md:text-xl text-brand-navy-200 leading-relaxed font-medium">
            {renderIntroWithHomeLink(service.intro)}
          </p>
          <div className="mt-8 flex justify-center">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 rounded bg-brand-orange-500 px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-brand-orange-600 shadow-md"
            >
              Få et uforpligtende tilbud
            </Link>
          </div>
        </div>
      </section>

      {/* Main Content Grid */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
          {/* Left Column: Description & When to Choose */}
          <div className="lg:col-span-2 space-y-12">
            {slug === 'draen-etablering-reparation' ? (
              <DraenContentSection />
            ) : slug === 'kloakspuling' ? (
              <KloakspulingContentSection />
            ) : slug === 'stoebearbejde' ? (
              <StoebearbejdeContentSection />
            ) : slug === 'belaegning' ? (
              <BelaegningContentSection />
            ) : (
              <>
                {/* Description */}
                <div>
                  <h2 className="font-display text-2xl font-bold text-brand-navy-900 mb-6 flex items-center gap-2 pb-3 border-b border-brand-navy-100">
                    {getHeadingBySlug(slug, service.title)}
                  </h2>
                  <div 
                    className="max-w-none text-brand-navy-700 leading-relaxed space-y-4 [&_p]:text-base [&_p]:sm:text-lg [&_p]:leading-relaxed [&_p]:text-brand-navy-700 [&_p]:mb-4 [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-brand-navy-900 [&_h2]:mt-8 [&_h2]:mb-3 [&_h3]:font-display [&_h3]:text-xl [&_h3]:sm:text-2xl [&_h3]:font-bold [&_h3]:text-brand-navy-900 [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:border-l-4 [&_h3]:border-brand-orange-500 [&_h3]:pl-3.5 [&_h3]:pt-0.5 [&_ul]:my-5 [&_ul]:space-y-2.5 [&_ul]:bg-white [&_ul]:p-5 [&_ul]:rounded-lg [&_ul]:border [&_ul]:border-brand-navy-200/80 [&_ul]:shadow-xs [&_ul]:list-disc [&_ul]:list-inside [&_li]:text-brand-navy-800 [&_li]:marker:text-brand-orange-500 [&_li]:marker:text-base [&_li]:leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: cleanDescription(service.description) }}
                  />

                  {/* Bulletpoints for what we offer within this service */}
                  {getWhatWeOfferBulletpoints(slug) && (
                    <div className="mt-8 pt-8 border-t border-brand-navy-100">
                      <h3 className="font-display text-lg font-bold text-brand-navy-900 mb-4">
                        Vi tilbyder blandt andet:
                      </h3>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                        {getWhatWeOfferBulletpoints(slug).map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2.5 text-brand-navy-700 text-sm md:text-base">
                            <i className="fa-solid fa-check text-brand-orange-500 mt-1 shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* When to Choose */}
                {service.when_to_choose && (
                  <div>
                    <h2 className="font-display text-2xl font-bold text-brand-navy-900 mb-6 flex items-center gap-2 pb-3 border-b border-brand-navy-100">
                      <i className="fa-solid fa-circle-question text-brand-orange-500" /> Hvornår skal du vælge dette?
                    </h2>
                    <div 
                      className="max-w-none text-brand-navy-700 leading-relaxed space-y-4 [&_p]:text-base [&_p]:leading-relaxed [&_p]:text-brand-navy-700 [&_ul]:my-4 [&_ul]:space-y-2 [&_ul]:bg-white [&_ul]:p-5 [&_ul]:rounded-lg [&_ul]:border [&_ul]:border-brand-navy-200/80 [&_ul]:shadow-xs [&_ul]:list-disc [&_ul]:list-inside [&_li]:text-brand-navy-800 [&_li]:marker:text-brand-orange-500"
                      dangerouslySetInnerHTML={{ __html: service.when_to_choose }}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Right Column: Benefits & Contact Sidebar */}
          <div className="space-y-8">
            {/* Benefits Card */}
            {service.benefits && (
              <div className="bg-brand-navy-900 text-white p-6 md:p-8 shadow-md relative overflow-hidden">
                <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-brand-orange-500/10" />
                <h3 className="font-display text-xl font-bold uppercase tracking-wide text-white mb-6 flex items-center gap-2">
                  <i className="fa-solid fa-circle-check" /> Dine fordele
                </h3>
                <div 
                  className="prose prose-invert max-w-none text-brand-navy-200 text-sm leading-relaxed benefits-list [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-2"
                  dangerouslySetInnerHTML={{ __html: service.benefits }}
                />
              </div>
            )}

            {/* Direct Contact Sidebar Card */}
            <div className="bg-white p-6 md:p-8 shadow-sm border-2 border-brand-orange-500/20 relative">
              <div className="absolute -top-3 left-6 rounded bg-brand-orange-500 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">
                Autoriseret Kloakmester
              </div>
              <h3 className="font-display text-xl font-bold text-brand-navy-900 mt-2">
                Skal vi hjælpe dig?
              </h3>
              <p className="mt-3 text-sm text-brand-navy-600 leading-relaxed">
                Kontakt os i dag for en uforpligtende snak om dit projekt. Vi hjælper kunder i Sommersted, Haderslev, Vojens, Vejen, Kolding, Gram og omegn.
              </p>
              <div className="mt-6 space-y-3">
                <a
                  href="tel:+4520483818"
                  className="flex items-center justify-center gap-2 rounded bg-brand-navy-900 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-brand-navy-800 w-full"
                >
                  <i className="fa-solid fa-phone text-brand-orange-400" /> Ring: 20 48 38 18
                </a>
                <Link
                  to="/kontakt"
                  className="flex items-center justify-center gap-2 rounded border border-brand-navy-200 bg-brand-navy-50 py-3 font-display text-sm font-bold uppercase tracking-wider text-brand-navy-900 transition-colors hover:bg-brand-navy-100 w-full"
                >
                  <i className="fa-solid fa-envelope text-brand-orange-500" /> Skriv besked
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Matching Gallery Section */}
      {matchedGallery && matchedGallery.images && matchedGallery.images.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-navy-900 mb-2 pb-3 border-b border-brand-navy-100">
            Billeder fra vores arbejde inden for {service.title.toLowerCase()}
          </h2>
          {matchedGallery.description?.da && (
            <p className="text-sm text-brand-navy-600 mb-6 leading-relaxed">
              {matchedGallery.description.da}
            </p>
          )}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {matchedGallery.images.map((imgUrl, idx) => {
              const imgMeta = getImageMeta(imgUrl, matchedGallery.title, idx + 1);
              return (
                <figure key={idx} className="group flex flex-col">
                  <button
                    type="button"
                    onClick={() => setLightboxImage({ url: imgUrl, ...imgMeta })}
                    className="relative aspect-video w-full overflow-hidden rounded-lg border border-brand-navy-100 bg-brand-navy-50 transition-transform hover:scale-[1.02] hover:shadow-md group cursor-pointer text-left"
                    title={imgMeta.title}
                    aria-label={`Vis forstørrelse af ${imgMeta.title}`}
                  >
                    <img
                      src={`https://assets.auxodev.dk/cdn-cgi/image/width=400,height=250,fit=cover,quality=80,format=auto/${imgUrl.replace('https://assets.auxodev.dk/', '')}`}
                      alt={imgMeta.alt}
                      title={imgMeta.title}
                      className="h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-brand-navy-900/60 opacity-0 transition-opacity group-hover:opacity-100 flex flex-col justify-between p-3 text-white">
                      <span className="text-xs font-semibold line-clamp-2">{imgMeta.title}</span>
                      <div className="flex items-center justify-end text-brand-orange-400">
                        <i className="fa-solid fa-magnifying-glass-plus text-base" />
                      </div>
                    </div>
                  </button>
                  <figcaption className="mt-1.5 px-0.5">
                    <span className="block text-xs font-semibold text-brand-navy-900 line-clamp-1">{imgMeta.title}</span>
                    {imgMeta.description && (
                      <span className="block text-[11px] text-brand-navy-600 line-clamp-1 mt-0.5" title={imgMeta.description}>
                        {imgMeta.description}
                      </span>
                    )}
                  </figcaption>
                </figure>
              );
            })}
          </div>
        </section>
      )}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy-950/90 p-4 backdrop-blur-sm cursor-zoom-out"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 text-white hover:text-brand-orange-400 text-3xl p-2 cursor-pointer z-10"
            aria-label="Luk forstørret visning"
          >
            <i className="fa-solid fa-xmark" />
          </button>
          <div
            className="relative max-h-[90vh] max-w-4xl w-full flex flex-col items-center cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxImage.url || lightboxImage}
              alt={lightboxImage.alt || 'Forstørret visning af udført arbejde'}
              title={lightboxImage.title || 'Udført arbejde - Entreprenør Jan Nørgaard ApS'}
              className="max-h-[75vh] w-auto max-w-full object-contain rounded-t-lg shadow-2xl"
            />
            {lightboxImage.title && (
              <div className="w-full bg-brand-navy-900 text-white p-4 rounded-b-lg border-t border-brand-navy-700">
                <h4 className="font-display text-base font-bold text-white mb-1">
                  {lightboxImage.title}
                </h4>
                {lightboxImage.description && (
                  <p className="text-sm text-brand-navy-200">
                    {lightboxImage.description}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
